import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '@/store';
import {
  fetchISAllocations,
  bulkSaveAllocations,
  createAllocation,
  setSelectedIS,
  setScenarioId,
  updateCellValue,
  clearPendingUpdates
} from '@/store/slices/isAllocationGridSlice';
import { apiCall } from '@/utils/api';
import { cn } from '@/utils/cn';
import {
  Maximize2,
  Minimize2,
  Grid3x3,
  TrendingUp,
  AlertTriangle,
  DollarSign,
  Save,
  X,
  Plus,
} from 'lucide-react';
import {
  GlassPageHeader,
  GlassCard,
  GlassButton,
  GlassSelect,
  GlassInput,
  GlassAlert,
  GlassModal,
  GlassLoadingSpinner,
  MetricCard,
  SectionReveal,
  CollapsibleMetricsSection,
} from '@/design-system';

interface EditableCellProps {
  value: number;
  isDirty?: boolean;
  disabled?: boolean;
  onCommit: (value: number) => void;
}

const EditableAllocationCell = React.memo<EditableCellProps>(
({
  value,
  isDirty,
  disabled,
  onCommit
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const handleCommit = useCallback(() => {
    if (draft !== value) {
      onCommit(draft);
    }
    setIsEditing(false);
  }, [draft, value, onCommit]);

  const handleCancel = useCallback(() => {
    setDraft(value);
    setIsEditing(false);
  }, [value]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleCommit();
    if (e.key === 'Escape') handleCancel();
  }, [handleCommit, handleCancel]);

  if (disabled) {
    return <div className="text-center text-muted-foreground/60">-</div>;
  }

  return (
    <div
      className={cn(
        'text-center cursor-pointer hover:bg-bg-hover transition-colors rounded px-2 py-1',
        isDirty && 'bg-status-warning-bg ring-1 ring-status-warning'
      )}
      onClick={() => !disabled && setIsEditing(true)}
    >
      {isEditing ? (
        <input
          type="number"
          min="0"
          step="0.5"
          value={draft}
          onChange={(e) => setDraft(Number(e.target.value))}
          onBlur={handleCommit}
          onKeyDown={handleKeyDown}
          className="w-full text-center border-none bg-transparent focus:outline-none text-foreground"
          autoFocus
        />
      ) : (
        <span className="text-foreground">{value || '-'}</span>
      )}
    </div>
  );
},
(prevProps, nextProps) => {
  return (
    prevProps.value === nextProps.value &&
    prevProps.isDirty === nextProps.isDirty &&
    prevProps.disabled === nextProps.disabled
  );
});

EditableAllocationCell.displayName = 'EditableAllocationCell';

interface ColumnWidths {
  [key: string]: number;
}

const ISAllocationGridPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    selectedISId,
    scenarioId,
    investibleStructure,
    epics,
    programs,
    summary,
    pendingUpdates,
    dirtyCells,
    isLoading,
    isSaving,
    error
  } = useSelector((state: RootState) => state.isAllocationGrid);

  const [investibleStructures, setInvestibleStructures] = useState<Array<{ id: string; name: string; category: string }>>([]);
  const [scenarios, setScenarios] = useState<Array<{ id: string; name: string }>>([]);
  const [isMaximized, setIsMaximized] = useState(false);
  const [columnWidths, setColumnWidths] = useState<ColumnWidths>({
    epicName: 250,
    priority: 80,
    demand: 100,
    supply: 100
  });
  const [resizing, setResizing] = useState<{ column: string; startX: number; startWidth: number } | null>(null);

  // New allocation modal state
  const [newAllocationModal, setNewAllocationModal] = useState<{
    epicId: string;
    epicName: string;
    programId: string;
    teamId: string;
    teamName: string;
  } | null>(null);
  const [newTeamWeeks, setNewTeamWeeks] = useState<number>(0);
  const [newQuarter, setNewQuarter] = useState<number>(1);

  const [overAllocWarningDismissed, setOverAllocWarningDismissed] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    apiCall('GET', '/investible-structures')
      .then(res => {
        if (res.success && res.data) {
          setInvestibleStructures((res.data as any).investibleStructures || []);
        }
      })
      .catch(err => console.error('Failed to fetch ISs', err));

    apiCall('GET', '/scenarios')
      .then(res => {
        if (res.success && res.data) {
          setScenarios((res.data as any).scenarios || []);
        }
      })
      .catch(err => console.error('Failed to fetch scenarios', err));
  }, []);

  useEffect(() => {
    if (selectedISId) {
      dispatch(fetchISAllocations({ isId: selectedISId, scenarioId }));
    }
  }, [selectedISId, scenarioId, dispatch]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (resizing) {
        const delta = e.clientX - resizing.startX;
        const newWidth = Math.max(50, resizing.startWidth + delta);
        setColumnWidths(prev => ({
          ...prev,
          [resizing.column]: newWidth
        }));
      }
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    if (resizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing]);

  const handleSave = async () => {
    const updates = Object.entries(pendingUpdates).map(([id, teamWeeks]) => ({
      id,
      teamWeeks
    }));

    try {
      await dispatch(bulkSaveAllocations(updates)).unwrap();
      if (selectedISId) {
        dispatch(fetchISAllocations({ isId: selectedISId, scenarioId }));
      }
    } catch (error) {
      console.error('Failed to save allocations', error);
    }
  };

  const startResize = useCallback((column: string, e: React.MouseEvent) => {
    e.preventDefault();
    setResizing({
      column,
      startX: e.clientX,
      startWidth: columnWidths[column] || 100
    });
  }, [columnWidths]);

  const handleCellCommit = useCallback((epicId: string, programId: string, teamId: string, value: number) => {
    dispatch(updateCellValue({
      epicId,
      programId,
      teamId,
      value
    }));
  }, [dispatch]);

  const handleCreateAllocation = useCallback(async () => {
    if (!newAllocationModal || newTeamWeeks <= 0) return;
    try {
      await dispatch(createAllocation({
        epicId: newAllocationModal.epicId,
        programId: newAllocationModal.programId,
        teamId: newAllocationModal.teamId,
        allocatedTeamWeeks: newTeamWeeks,
        quarter: newQuarter,
      })).unwrap();
      // Refetch grid data to show the new allocation
      if (selectedISId) {
        dispatch(fetchISAllocations({ isId: selectedISId, scenarioId }));
      }
      // Close modal and reset
      setNewAllocationModal(null);
      setNewTeamWeeks(0);
      setNewQuarter(1);
    } catch (err) {
      console.error('Failed to create allocation', err);
    }
  }, [newAllocationModal, newTeamWeeks, newQuarter, selectedISId, scenarioId, dispatch]);

  const pendingCount = Object.keys(pendingUpdates).length;

  const hasOverAllocatedTeams = useMemo(() =>
    programs.some(p => p.teams.some(t => t.globalAllocatedTWs > t.memberCount * 52)),
    [programs]
  );

  // Build IS options for GlassSelect
  const isOptions = investibleStructures.map(is => ({
    value: is.id,
    label: `${is.name} (${is.category})`
  }));

  // Build scenario options for GlassSelect
  const scenarioOptions = [
    { value: '', label: 'Baseline' },
    ...scenarios.map(s => ({ value: s.id, label: s.name }))
  ];

  return (
    <div className={cn(
      isMaximized ? 'fixed inset-0 z-50 overflow-hidden bg-bg-primary' : 'min-h-screen glass-page-bg p-6 lg:p-8'
    )}>
      {/* Header */}
      <SectionReveal delay={0}>
        <GlassPageHeader
          title="Allocation Grid by Investible Structure"
          subtitle="Select an IS to view and edit Team Week allocations for its EPICs"
          icon={<Grid3x3 className="w-6 h-6" />}
          iconGradient="blue-violet"
          className={cn(isMaximized ? 'mx-6 mt-4' : 'mb-6')}
          actions={
            <GlassButton
              onClick={() => setIsMaximized(!isMaximized)}
              variant="secondary"
              size="sm"
              leftIcon={isMaximized ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            >
              {isMaximized ? 'Exit Fullscreen' : 'Fullscreen'}
            </GlassButton>
          }
        />
      </SectionReveal>

      {/* Filters */}
      <SectionReveal delay={0.1}>
        <GlassCard
          variant="default"
          padding="lg"
          className={cn(isMaximized ? 'mx-6 mt-4' : 'mb-6')}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <GlassSelect
              label="Investible Structure *"
              value={selectedISId || ''}
              onChange={(value) => dispatch(setSelectedIS(value || null))}
              options={isOptions}
              placeholder="-- Select IS --"
              fullWidth
            />
            <GlassSelect
              label="Scenario (Optional)"
              value={scenarioId || ''}
              onChange={(value) => dispatch(setScenarioId(value || null))}
              options={scenarioOptions}
              fullWidth
            />
          </div>
        </GlassCard>
      </SectionReveal>

      {/* Error Message */}
      {error && (
        <SectionReveal delay={0.15}>
          <div className={cn(isMaximized ? 'mx-6 mb-4' : 'mb-6')}>
            <GlassAlert variant="error" message={error} />
          </div>
        </SectionReveal>
      )}

      {/* Loading State */}
      {isLoading && (
        <SectionReveal delay={0.2}>
          <GlassCard
            variant="default"
            padding="lg"
            className={cn(isMaximized && 'mx-6')}
          >
            <div className="p-8 flex flex-col items-center justify-center">
              <GlassLoadingSpinner size="lg" variant="gradient" label="Loading allocation data..." />
            </div>
          </GlassCard>
        </SectionReveal>
      )}

      {/* Over-allocation Warning (one-time dismissible) */}
      {!isLoading && hasOverAllocatedTeams && !overAllocWarningDismissed && (
        <div className={cn(
          'flex items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3',
          isMaximized ? 'mx-6 mb-4' : 'mb-6'
        )}>
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
          <span className="text-sm text-amber-200">
            Some teams are over-allocated across all Investible Structures. Over-allocated columns are highlighted in red.
          </span>
          <button
            onClick={() => setOverAllocWarningDismissed(true)}
            className="ml-auto text-amber-400 hover:text-amber-300 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Grid Content */}
      {!isLoading && investibleStructure && (
        <>
          {/* Metrics Row */}
          <SectionReveal delay={0.2}>
            <CollapsibleMetricsSection storageKey="is-allocation">
              <div className={cn(
                'grid grid-cols-2 lg:grid-cols-4 gap-4',
                isMaximized && 'mx-6'
              )}>
                <MetricCard
                  title="Total Demand"
                  value={`${summary.totalDemand} TW`}
                  subtitle={investibleStructure.name}
                  icon={<TrendingUp className="w-5 h-5" />}
                  iconColor="cyan"
                />
                <MetricCard
                  title="Total Supply"
                  value={`${summary.totalSupply} TW`}
                  subtitle={`${investibleStructure.category}`}
                  icon={<Grid3x3 className="w-5 h-5" />}
                  iconColor="blue"
                />
                <MetricCard
                  title="Variance"
                  value={`${summary.variance} TW`}
                  subtitle={summary.variance < 0 ? 'Under-allocated' : summary.variance > 0 ? 'Over-allocated' : 'Balanced'}
                  icon={<AlertTriangle className="w-5 h-5" />}
                  iconColor={summary.variance < 0 ? 'rose' : summary.variance > 0 ? 'amber' : 'emerald'}
                />
                <MetricCard
                  title="Total Cost"
                  value={`$${(summary.totalCost / 1000000).toFixed(2)}M`}
                  subtitle="estimated"
                  icon={<DollarSign className="w-5 h-5" />}
                  iconColor="amber"
                />
              </div>
            </CollapsibleMetricsSection>
          </SectionReveal>

          {/* Allocation Grid */}
          <SectionReveal delay={0.3}>
            <GlassCard
              variant="default"
              padding="none"
              ref={tableContainerRef}
              className={cn(
                isMaximized ? 'mx-6 flex-1 flex flex-col overflow-hidden' : 'overflow-hidden'
              )}
              style={isMaximized ? { height: 'calc(100vh - 400px)' } : {}}
            >
              {/* Scrollable Table Wrapper */}
              <div className="overflow-auto flex-1" style={{ maxHeight: isMaximized ? '100%' : '600px' }}>
                <table className="min-w-full border-collapse" style={{ tableLayout: 'fixed' }}>
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-bg-secondary/95 backdrop-blur-sm border-b border-border-primary">
                      {/* Fixed Columns */}
                      <th
                        className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider border-r border-border-primary bg-bg-secondary/95 relative group"
                        style={{ width: columnWidths.epicName }}
                      >
                        <div className="flex items-center justify-between">
                          <span>Epic Name</span>
                          <div
                            className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-accent-blue opacity-0 group-hover:opacity-100 transition-opacity"
                            onMouseDown={(e) => startResize('epicName', e)}
                          />
                        </div>
                      </th>
                      <th
                        className="px-4 py-3 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-bg-secondary/95 relative group"
                        style={{ width: columnWidths.priority }}
                      >
                        <div className="flex items-center justify-center">
                          <span>Priority</span>
                          <div
                            className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-accent-blue opacity-0 group-hover:opacity-100 transition-opacity"
                            onMouseDown={(e) => startResize('priority', e)}
                          />
                        </div>
                      </th>
                      <th
                        className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-bg-secondary/95 relative group"
                        style={{ width: columnWidths.demand }}
                      >
                        <div className="flex items-center justify-end">
                          <span>Demand (TW)</span>
                          <div
                            className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-accent-blue opacity-0 group-hover:opacity-100 transition-opacity"
                            onMouseDown={(e) => startResize('demand', e)}
                          />
                        </div>
                      </th>
                      <th
                        className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider border-r border-border-primary bg-bg-secondary/95 relative group"
                        style={{ width: columnWidths.supply }}
                      >
                        <div className="flex items-center justify-end">
                          <span>Supply (TW)</span>
                          <div
                            className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-accent-blue opacity-0 group-hover:opacity-100 transition-opacity"
                            onMouseDown={(e) => startResize('supply', e)}
                          />
                        </div>
                      </th>

                      {/* Program/Team Columns */}
                      {programs.map(program => (
                        <th
                          key={program.id}
                          colSpan={program.teams.length}
                          className="px-2 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider bg-gradient-to-r from-[var(--accent)] to-accent-violet border-r border-[var(--accent)]/50 sticky top-0"
                        >
                          {program.name}
                        </th>
                      ))}
                    </tr>
                    <tr className="bg-bg-tertiary/95 backdrop-blur-sm sticky" style={{ top: '48px' }}>
                      <th colSpan={4} className="bg-bg-tertiary/95 border-b border-border-primary"></th>
                      {programs.map(program =>
                        program.teams.map(team => {
                          const capacity = team.memberCount * 52;
                          const allocated = team.globalAllocatedTWs;
                          const isOverAllocated = allocated > capacity;
                          return (
                            <th
                              key={`${program.id}-${team.id}`}
                              className={cn(
                                "px-2 py-2 text-center text-xs font-medium border-b border-border-primary",
                                isOverAllocated
                                  ? "bg-status-error/15 text-status-error"
                                  : "text-muted-foreground bg-accent-blue-soft"
                              )}
                              style={{ minWidth: '80px' }}
                            >
                              <div>{team.name}</div>
                              <div className={cn(
                                "text-[10px] mt-0.5 font-normal tabular-nums whitespace-nowrap",
                                isOverAllocated ? "text-status-error font-semibold" : "text-muted-foreground/70"
                              )}>
                                {capacity} / {allocated} TW
                              </div>
                            </th>
                          );
                        })
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-primary">
                    {epics.map(epic => (
                      <tr key={epic.id} className="hover:bg-bg-hover transition-colors">
                        {/* Fixed Cells */}
                        <td
                          className="px-4 py-3 text-sm font-medium text-foreground border-r border-border-primary overflow-hidden text-ellipsis whitespace-nowrap"
                          style={{ width: columnWidths.epicName }}
                          title={epic.name}
                        >
                          {epic.name}
                        </td>
                        <td
                          className="px-4 py-3 text-sm text-center text-muted-foreground"
                          style={{ width: columnWidths.priority }}
                        >
                          {epic.priority ? (
                            <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-accent-violet-soft text-accent-violet">
                              P{epic.priority}
                            </span>
                          ) : (
                            <span className="text-muted-foreground/60">-</span>
                          )}
                        </td>
                        <td
                          className="px-4 py-3 text-sm text-right text-foreground font-semibold tabular-nums"
                          style={{ width: columnWidths.demand }}
                        >
                          {epic.requestedTeamWeeks}
                        </td>
                        <td
                          className="px-4 py-3 text-sm text-right font-semibold border-r border-border-primary tabular-nums"
                          style={{ width: columnWidths.supply }}
                        >
                          <span className={cn(
                            epic.allocatedTeamWeeks < epic.requestedTeamWeeks
                              ? 'text-status-error'
                              : epic.allocatedTeamWeeks > epic.requestedTeamWeeks
                              ? 'text-status-warning'
                              : 'text-status-success'
                          )}>
                            {epic.allocatedTeamWeeks}
                          </span>
                        </td>

                        {/* Allocation Cells */}
                        {programs.map(program =>
                          program.teams.map(team => {
                            const allocation = epic.allocations[program.id]?.[team.id];
                            const cellKey = `${epic.id}-${program.id}-${team.id}`;
                            const isDirty = dirtyCells[cellKey];

                            return (
                              <td
                                key={`${epic.id}-${program.id}-${team.id}`}
                                className="px-2 py-2 text-sm"
                                style={{ minWidth: '80px' }}
                              >
                                {allocation ? (
                                  <EditableAllocationCell
                                    value={allocation.teamWeeks}
                                    isDirty={isDirty}
                                    onCommit={(value) =>
                                      handleCellCommit(epic.id, program.id, team.id, value)
                                    }
                                  />
                                ) : (
                                  <div
                                    className="text-center text-muted-foreground/30 cursor-pointer hover:bg-accent-blue/10 hover:text-accent-blue transition-colors rounded px-2 py-1 group/cell"
                                    onClick={() => setNewAllocationModal({
                                      epicId: epic.id,
                                      epicName: epic.name,
                                      programId: program.id,
                                      teamId: team.id,
                                      teamName: team.name,
                                    })}
                                    title="Click to add allocation"
                                  >
                                    <Plus className="w-3.5 h-3.5 mx-auto opacity-0 group-hover/cell:opacity-100 transition-opacity" />
                                  </div>
                                )}
                              </td>
                            );
                          })
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </GlassCard>
          </SectionReveal>

          {/* Floating Save Panel */}
          {pendingCount > 0 && (
            <div className={cn(
              'fixed glass-strong shadow-2xl rounded-xl p-4 border-2 border-accent-blue',
              isMaximized ? 'bottom-6 right-12' : 'bottom-6 right-6'
            )}
            style={{ zIndex: 60 }}
            >
              <p className="text-sm font-semibold text-foreground mb-3">
                {pendingCount} unsaved {pendingCount === 1 ? 'change' : 'changes'}
              </p>
              <div className="flex gap-2">
                <GlassButton
                  onClick={handleSave}
                  disabled={isSaving}
                  isLoading={isSaving}
                  variant="primary"
                  size="sm"
                  leftIcon={<Save className="w-4 h-4" />}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </GlassButton>
                <GlassButton
                  onClick={() => {
                    if (window.confirm('Discard all unsaved changes?')) {
                      dispatch(clearPendingUpdates());
                    }
                  }}
                  variant="ghost"
                  size="sm"
                  disabled={isSaving}
                  leftIcon={<X className="w-4 h-4" />}
                >
                  Discard
                </GlassButton>
              </div>
            </div>
          )}
        </>
      )}

      {/* New Allocation Modal */}
      <GlassModal
        isOpen={!!newAllocationModal}
        onClose={() => {
          setNewAllocationModal(null);
          setNewTeamWeeks(0);
          setNewQuarter(1);
        }}
        title="Add Team Allocation"
        subtitle={newAllocationModal ? `${newAllocationModal.epicName} \u2192 ${newAllocationModal.teamName}` : ''}
        size="sm"
        footer={
          <div className="flex gap-3 justify-end">
            <GlassButton
              variant="ghost"
              size="sm"
              onClick={() => {
                setNewAllocationModal(null);
                setNewTeamWeeks(0);
                setNewQuarter(1);
              }}
            >
              Cancel
            </GlassButton>
            <GlassButton
              variant="primary"
              size="sm"
              onClick={handleCreateAllocation}
              disabled={newTeamWeeks <= 0 || isSaving}
              isLoading={isSaving}
              leftIcon={<Plus className="w-4 h-4" />}
            >
              Add Allocation
            </GlassButton>
          </div>
        }
      >
        <div className="space-y-4">
          <GlassInput
            label="Team Weeks"
            type="number"
            value={newTeamWeeks}
            onChange={(value) => setNewTeamWeeks(Number(value))}
            min={0}
            step={0.5}
            placeholder="Enter team weeks"
            fullWidth
          />
          <GlassSelect
            label="Target Quarter"
            value={String(newQuarter)}
            onChange={(value) => setNewQuarter(Number(value))}
            options={[
              { value: '1', label: 'Q1' },
              { value: '2', label: 'Q2' },
              { value: '3', label: 'Q3' },
              { value: '4', label: 'Q4' },
            ]}
            fullWidth
          />
        </div>
      </GlassModal>

      {/* Empty State */}
      {!isLoading && !selectedISId && (
        <SectionReveal delay={0.2}>
          <GlassCard
            variant="default"
            padding="lg"
            className={cn('text-center py-16', isMaximized && 'mx-6')}
          >
            <Grid3x3 className="w-16 h-16 mx-auto text-muted-foreground/60 mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              No Investible Structure Selected
            </h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              Select an Investible Structure from the dropdown above to view and edit Team Week allocations for its EPICs.
            </p>
          </GlassCard>
        </SectionReveal>
      )}
    </div>
  );
};

export default ISAllocationGridPage;
